import "leaflet/dist/leaflet.css";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  Icon,
  IconButton,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Popover,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { capitalCase } from "change-case";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Modal from "modules/Modal/Modal";
import CreateUpdateCustomEvent from "modules/CustomEvent/CreateUpdateCustomEvent";
import ScheduleJobPhase from "modules/jobs/ScheduleJobPhase/ScheduleJobPhase";
import ScheduleFilters from "modules/jobs/JobsSchedule/ScheduleFilters";
import JobsSchedulesCalendar from "modules/jobs/JobsSchedulesCalendar/JobsSchedulesCalendar";
import JobModal from "components/JobModal/JobModal";
import useGetAllCrews from "hooks/crews/useGetAllCrews";
import useGetAllOrganizationUsers from "hooks/users/useGetAllOrganizationUsers";
import useModalState from "hooks/useModalState";
import { useCalendarContext } from "modules/jobs/JobsSchedule/CalendarContext";
import { JobPhaseStatus, JobStatus } from "generated/graphql";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import StatusChip, { STATUS_STYLES } from "./StatusChip";
import { groupPhasesByProductGroup } from "./groupPhases";
import OrganizationProductTypeIdsOptions from "modules/OrganizationProductTypes/OrganizationProductTypeIdsOptions/OrganizationProductTypeIdsOptions";
import { enumToValueOptions } from "utils/enums/enumToValueOptions";
import useJobsTableData from "hooks/jobs/useJobsTableData";

dayjs.extend(utc);

const FALLBACK_CENTER: [number, number] = [39.8283, -98.5795];

const phaseStatusSelectStyles = {
  minWidth: 240,
  minHeight: "2.5em",
  backgroundColor: "white!important",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "grey.300",
  },
  "& .MuiSelect-select": {
    py: 1.25,
    minHeight: "2.5em",
    display: "flex",
    alignItems: "center",
  },
};

function createJobMarkerIcon(status: string, phasesCount: number, selected: boolean) {
  const statusStyle = STATUS_STYLES[status] || {
    bgcolor: "#e5e7eb",
    color: "#475467",
  };
  const backgroundColor = selected ? statusStyle.color : statusStyle.bgcolor;
  const borderColor = statusStyle.color;
  const textColor = selected ? "#ffffff" : statusStyle.color;

  return L.divIcon({
    className: "",
    html: `<div style="display:flex;align-items:center;justify-content:center;width:34px;height:34px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${backgroundColor};border:2px solid ${borderColor};box-shadow:0 8px 18px rgba(15,23,42,0.28);"><span style="transform:rotate(45deg);color:${textColor};font-size:12px;font-weight:700;">${phasesCount}</span></div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 30],
    popupAnchor: [0, -28],
  });
}

function FitMapToMarkers({
  positions,
  selectedJobId,
  selectedPosition,
}: {
  positions: [number, number][];
  selectedJobId: string | null;
  selectedPosition: [number, number] | null;
}) {
  const map = useMap();
  const hasInitializedRef = useRef(false);
  const previousSelectedJobIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (selectedJobId && selectedPosition) {
      if (previousSelectedJobIdRef.current !== selectedJobId) {
        map.flyTo(selectedPosition, map.getZoom(), {
          duration: 0.35,
        });
      }
      previousSelectedJobIdRef.current = selectedJobId;
      hasInitializedRef.current = true;
      return;
    }

    previousSelectedJobIdRef.current = null;

    if (hasInitializedRef.current || positions.length === 0) {
      return;
    }

    if (positions.length === 1) {
      map.setView(positions[0], 11);
    } else {
      map.fitBounds(positions, {
        padding: [40, 40],
        maxZoom: 11,
      });
    }

    hasInitializedRef.current = true;
  }, [map, positions, selectedJobId, selectedPosition]);

  return null;
}

function JobSummaryCard({
  job,
  onJobIdClick,
}: {
  job: any;
  onJobIdClick: (jobId: string) => void;
}) {
  return (
    <MDBox
      sx={{
        border: "1px solid",
        borderColor: "grey.300",
        borderRadius: "8px",
        backgroundColor: "white!important",
        p: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
          <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
            JOB ID :{" "}
            <Typography
              component="span"
              variant="body2"
              onClick={() => onJobIdClick(job.id)}
              sx={{
                lineHeight: 1.4,
                color: "#1976d2",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {job.externalId}
            </Typography>
          </Typography>
          <StatusChip status={job.status} />
        </Box>
      </Box>

      {(job.company?.name || job.proposalStage?.name) && (
        <Typography variant="body2" sx={{ display: "block", mb: 1, lineHeight: 1.4 }}>
          {job.company?.name} - {job.proposalStage?.name}
        </Typography>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        {job.address?.city && job.address?.state ? `${job.address.city}, ${job.address.state}` : ""}
      </Typography>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        Won Date
      </Typography>
      <Typography variant="body2" fontWeight="bold" sx={{ mb: 1, fontSize: "0.875rem" }}>
        {job.proposal?.wonAt ? dayjs(job.proposal.wonAt).format("MM/DD/YYYY") : "Not set"}
      </Typography>

      <Typography sx={{ fontSize: "0.875rem" }}>Job Total</Typography>
      <Typography
        variant="body2"
        fontWeight="bold"
        sx={{ mb: 0, fontSize: "0.875rem", color: "#0d804a" }}
      >
        {formatCentsToUSD(parseInt(job.proposedOverallTotal, 10))}
      </Typography>
    </MDBox>
  );
}

function JobDetailsDrawer({
  job,
  open,
  onClose,
  onPhaseClick,
  onJobIdClick,
}: {
  job: any | null;
  open: boolean;
  onClose: () => void;
  onPhaseClick: (phaseId: string) => void;
  onJobIdClick: (jobId: string) => void;
}) {
  return (
    <Paper
      elevation={6}
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        bottom: 0,
        width: { xs: "100%", sm: 370, md: 410 },
        maxWidth: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 0,
        borderRight: "1px solid",
        borderColor: "grey.200",
        transform: open ? "translateX(0)" : "translateX(-100%)",
        transition: "transform 240ms ease",
        zIndex: 1000,
        pointerEvents: open ? "auto" : "none",
        backgroundColor: "#f8f9fa",
        overflow: "hidden",
      }}
    >
      <MDBox
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #e5e7eb",
          backgroundColor: "white!important",
        }}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>
            Job details
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <Icon>close</Icon>
        </IconButton>
      </MDBox>

      <MDBox
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {job ? (
          <>
            <JobSummaryCard job={job} onJobIdClick={onJobIdClick} />

            <MDBox>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>
                Job phases ({job.allPhases.length})
              </Typography>
              <MDBox sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {groupPhasesByProductGroup(job.allPhases).map((item, idx) => {
                  if (item.kind === "group") {
                    return (
                      <Box key={`group-${idx}`}>
                        <Box
                          sx={{
                            backgroundColor: "#f0f2f5",
                            borderRadius: "8px",
                            px: 1.5,
                            pt: 0.25,
                            pb: 0.75,
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            fontWeight="bold"
                            sx={{ fontSize: "0.75rem" }}
                          >
                            {item.groupName}
                          </Typography>
                        </Box>
                        <MDBox
                          sx={{
                            pl: 2,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            backgroundColor: "#f7f7f7",
                            borderRadius: "6px",
                          }}
                        >
                          {item.phases.map((phase: any) => (
                            <PhaseRow
                              key={phase.id}
                              job={job}
                              phase={phase}
                              onClick={onPhaseClick}
                            />
                          ))}
                        </MDBox>
                      </Box>
                    );
                  }
                  return (
                    <PhaseRow
                      key={item.phase.id}
                      job={job}
                      phase={item.phase}
                      onClick={onPhaseClick}
                    />
                  );
                })}
              </MDBox>
            </MDBox>
          </>
        ) : null}
      </MDBox>
    </Paper>
  );
}

function PhaseRow({
  job,
  phase,
  onClick,
}: {
  job: any;
  phase: any;
  onClick: (phaseId: string) => void;
}) {
  const isDraggable = phase.status === JobPhaseStatus.TO_BE_SCHEDULED;

  return (
    <Box
      draggable={isDraggable}
      data-draggable={isDraggable ? "true" : "false"}
      data-phase-data={
        isDraggable
          ? JSON.stringify({
              type: "jobPhase",
              phaseId: phase.id,
              jobId: job.id,
              phaseName: phase.productName || "Unknown",
              jobAddress: job.address?.line1 || "Unknown Address",
              jobExternalId: job.externalId,
              duration: phase.jobDuration || 0,
              total: phase.proposedOverallTotal,
            })
          : ""
      }
      onDragStart={(e) => {
        if (!isDraggable) {
          return;
        }

        const phaseData = {
          type: "jobPhase",
          phaseId: phase.id,
          jobId: job.id,
          phaseName: phase.productName || "Unknown",
          jobAddress: job.address?.line1 || "Unknown Address",
          jobExternalId: job.externalId,
          duration: phase.jobDuration || 0,
          total: phase.proposedOverallTotal,
        };

        e.dataTransfer.setData("text/plain", JSON.stringify(phaseData));
        e.dataTransfer.effectAllowed = "copy";
      }}
      onClick={() => onClick(phase.id)}
      sx={{
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: "8px",
        p: 1.5,
        cursor: "pointer",
        backgroundColor: "white",
        transition: "background-color 120ms ease, border-color 120ms ease",
        "&:hover": {
          backgroundColor: "#f8fafc",
          borderColor: "#cbd5e1",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          {isDraggable && (
            <Icon sx={{ fontSize: "16px!important", color: "#98a2b3" }}>drag_indicator</Icon>
          )}
          <Typography variant="caption" fontWeight="medium" sx={{ fontSize: "0.75rem" }}>
            {phase.productName || "Unknown"}
          </Typography>
        </Box>
        <StatusChip status={phase.status} />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 1,
          mt: 1,
        }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            Start Date
          </Typography>
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{ display: "block", fontSize: "0.75rem" }}
          >
            {phase.startsAt
              ? dayjs.utc(phase.startsAt).local().format("MM/DD/YYYY")
              : "Not scheduled"}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            Hours
          </Typography>
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{ display: "block", fontSize: "0.75rem" }}
          >
            {(phase.jobDuration || 0).toFixed(1)}
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            Total
          </Typography>
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{ display: "block", fontSize: "0.75rem", color: "#0d804a" }}
          >
            {formatCentsToUSD(phase.proposedOverallTotal)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function JobsScheduleMapView({
  jobs,
  jobsLoading,
  selectedProductTypes = [],
  selectedJobStatuses,
  onJobStatusesChange,
  onProductTypeChange,
  view,
  onViewChange,
}: {
  jobs: any[];
  jobsLoading: boolean;
  selectedProductTypes?: Array<{ label?: string; value: string | number }>;
  selectedJobStatuses: JobStatus[];
  onJobStatusesChange: (statuses: JobStatus[]) => void;
  onProductTypeChange: ReturnType<typeof useJobsTableData>["setOrganizationProductTypeIds"];
  view: "calendar" | "map";
  onViewChange: (view: "calendar" | "map") => void;
}) {
  const { data: crews, loading: crewsLoading } = useGetAllCrews();
  const { data: organizationUsers } = useGetAllOrganizationUsers();
  const { open, onClose, onOpen } = useModalState();
  const { triggerCalendarUpdate } = useCalendarContext();

  const [checked, setChecked] = useState<Record<string, any>>({});
  const [showCustom] = useState(true);
  const [selectedProjectManagers, setSelectedProjectManagers] = useState<string[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);
  const [openJobModal, setOpenJobModal] = useState(false);
  const [filtersAnchorEl, setFiltersAnchorEl] = useState<HTMLElement | null>(null);

  const selectedProductTypeIds = useMemo(
    () => new Set(selectedProductTypes.map((productType) => `${productType.value}`)),
    [selectedProductTypes]
  );

  useEffect(() => {
    if (!crews) {
      return;
    }

    setChecked(
      crews.reduce((acc, crew) => {
        acc[crew.id] = {
          ...crew,
          checked: true,
        };
        return acc;
      }, {} as Record<string, any>)
    );
  }, [crews]);

  const crewOptions = useMemo(() => {
    if (!crews) {
      return [];
    }

    return crews.map((crew) => ({
      label: crew.name,
      value: crew.id,
    }));
  }, [crews]);

  const projectManagerOptions = useMemo(() => {
    if (!organizationUsers) return [];
    return organizationUsers.map((user) => ({
      label: `${user.firstName} ${user.lastName}`,
      value: user.id,
    }));
  }, [organizationUsers]);

  const handleCrewCheckedChanged = useCallback((crew: any, isChecked: boolean) => {
    setChecked((prevValues) => ({
      ...prevValues,
      [crew.value]: {
        ...prevValues[crew.value],
        checked: isChecked,
      },
    }));
  }, []);

  const handleCrewFilterChange = useCallback(
    (selectedValues: string[]) => {
      crewOptions.forEach((crew) => {
        handleCrewCheckedChanged(crew, selectedValues.includes(crew.value));
      });
    },
    [crewOptions, handleCrewCheckedChanged]
  );

  const filteredMapJobs = useMemo(() => {
    const hasProductTypeFilter = selectedProductTypeIds.size > 0;

    return jobs
      .map((job) => {
        const phases = (job.jobPhases || []).filter((phase: any) => {
          const productTypeId = phase.organizationProductType?.id;
          const matchesProductType =
            !hasProductTypeFilter || (productTypeId && selectedProductTypeIds.has(productTypeId));

          return matchesProductType;
        });

        const latitude = Number.parseFloat(job.address?.latitude || "");
        const longitude = Number.parseFloat(job.address?.longitude || "");
        const hasCoordinates = Number.isFinite(latitude) && Number.isFinite(longitude);

        return {
          ...job,
          visiblePhases: phases,
          allPhases: job.jobPhases || [],
          latitude,
          longitude,
          hasCoordinates,
        };
      })
      .filter((job) => job.hasCoordinates && job.visiblePhases.length > 0);
  }, [jobs, selectedProductTypeIds]);

  const selectedJob = useMemo(
    () => filteredMapJobs.find((job) => job.id === selectedJobId) || null,
    [filteredMapJobs, selectedJobId]
  );

  useEffect(() => {
    if (selectedJobId && !selectedJob) {
      setSelectedJobId(null);
    }
  }, [selectedJobId, selectedJob]);

  const markerPositions = useMemo(
    () => filteredMapJobs.map((job) => [job.latitude, job.longitude] as [number, number]),
    [filteredMapJobs]
  );

  if (crewsLoading) {
    return (
      <MDBox
        sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}
      >
        <CircularProgress color="inherit" />
      </MDBox>
    );
  }

  return (
    <>
      <MDBox
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", xl: "minmax(360px, 0.8fr) minmax(520px, 1.2fr)" },
          gap: 2,
          height: "100%",
          p: 2,
          minHeight: 0,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: "14px",
            overflow: "hidden",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          <MDBox
            sx={{
              p: 2,
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
              backgroundColor: "white!important",
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ letterSpacing: "-0.02em", textTransform: "uppercase" }}
            >
              JOBS SCHEDULE
            </Typography>

            <MDBox display="flex" alignItems="center" gap={1} ml="auto">
              <IconButton
                onClick={(event) => setFiltersAnchorEl(event.currentTarget)}
                sx={{
                  width: 36,
                  height: 36,
                  border: "1px solid",
                  borderColor: "grey.300",
                  borderRadius: "10px",
                  backgroundColor: "white",
                }}
              >
                <Icon sx={{ fontSize: "18px!important" }}>filter_list</Icon>
              </IconButton>

              <ToggleButtonGroup
                exclusive
                size="small"
                value={view}
                onChange={(_, nextView: "calendar" | "map" | null) => {
                  if (nextView) {
                    onViewChange(nextView);
                  }
                }}
              >
                <ToggleButton value="calendar" aria-label="List view">
                  <Icon fontSize="small">view_list</Icon>
                </ToggleButton>
                <ToggleButton value="map" aria-label="Map view">
                  <Icon fontSize="small">map</Icon>
                </ToggleButton>
              </ToggleButtonGroup>
            </MDBox>
          </MDBox>

          <Popover
            open={!!filtersAnchorEl}
            anchorEl={filtersAnchorEl}
            onClose={() => setFiltersAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                mt: 1,
                p: 2,
                borderRadius: "14px",
                border: "1px solid",
                borderColor: "grey.200",
                backgroundColor: "#f7f9fa",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              },
            }}
          >
            <FormControl size="small">
              <Select
                multiple
                value={selectedJobStatuses}
                onChange={(e) => onJobStatusesChange(e.target.value as JobStatus[])}
                input={<OutlinedInput />}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return "Job status";
                  }
                  return selected.map((status) => capitalCase(status)).join(", ");
                }}
                sx={phaseStatusSelectStyles}
              >
                {enumToValueOptions(JobStatus).map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    <Checkbox checked={selectedJobStatuses.includes(status.value as JobStatus)} />
                    <ListItemText primary={capitalCase(status.label)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <OrganizationProductTypeIdsOptions sortByName={true}>
              {({ organizationProductTypeIdsOptions }) => (
                <FormControl size="small">
                  <Select
                    multiple
                    value={selectedProductTypes.map((typeId) => `${typeId.value}`)}
                    onChange={(e) => {
                      const result = organizationProductTypeIdsOptions.filter((option) =>
                        (e.target.value as string[]).includes(`${option.value}`)
                      );
                      onProductTypeChange(result);
                    }}
                    input={<OutlinedInput />}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return "Product type";
                      }

                      return selectedProductTypes.map((typeId) => typeId.label).join(", ");
                    }}
                    sx={phaseStatusSelectStyles}
                  >
                    {organizationProductTypeIdsOptions.map((option) => (
                      <MenuItem key={option.value} value={`${option.value}`}>
                        <Checkbox
                          checked={selectedProductTypes.some(
                            (typeId) => `${typeId.value}` === `${option.value}`
                          )}
                        />
                        <ListItemText primary={option.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </OrganizationProductTypeIdsOptions>
          </Popover>

          <MDBox sx={{ flex: 1, minHeight: 0, position: "relative", backgroundColor: "#e2e8f0" }}>
            <MapContainer
              center={FALLBACK_CENTER}
              zoom={4}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              <FitMapToMarkers
                positions={markerPositions}
                selectedJobId={selectedJobId}
                selectedPosition={
                  selectedJob ? [selectedJob.latitude, selectedJob.longitude] : null
                }
              />

              {filteredMapJobs.map((job) => (
                <Marker
                  key={job.id}
                  position={[job.latitude, job.longitude]}
                  icon={createJobMarkerIcon(
                    job.status,
                    job.visiblePhases.length,
                    selectedJobId === job.id
                  )}
                  eventHandlers={{
                    click: () => setSelectedJobId(job.id),
                  }}
                />
              ))}
            </MapContainer>

            {jobsLoading && (
              <MDBox
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255,255,255,0.92)",
                  zIndex: 900,
                }}
              >
                <CircularProgress size={28} />
              </MDBox>
            )}

            {!jobsLoading && filteredMapJobs.length === 0 && (
              <Paper
                elevation={0}
                sx={{
                  position: "absolute",
                  top: 24,
                  left: 24,
                  p: 2,
                  maxWidth: 300,
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                  No map results
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Adjust the map filters to show more jobs on the map.
                </Typography>
              </Paper>
            )}

            <JobDetailsDrawer
              job={selectedJob}
              open={!!selectedJob}
              onClose={() => setSelectedJobId(null)}
              onPhaseClick={(phaseId) => setSelectedPhaseId(phaseId)}
              onJobIdClick={() => setOpenJobModal(true)}
            />
          </MDBox>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: "14px",
            overflow: "hidden",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              p: 2,
              borderBottom: "1px solid #e5e7eb",
              flexShrink: 0,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <MDBox display="flex" gap={2} flexWrap="wrap" alignItems="center">
              <ScheduleFilters
                checked={checked}
                crewOptions={crewOptions}
                projectManagerOptions={projectManagerOptions}
                selectedProjectManagers={selectedProjectManagers}
                onCrewChange={handleCrewFilterChange}
                onProjectManagerChange={(selectedValues: string[]) =>
                  setSelectedProjectManagers(selectedValues)
                }
              />
              <MDButton color="dark" onClick={onOpen}>
                Add Event
              </MDButton>
            </MDBox>
          </MDBox>

          <MDBox
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              backgroundColor: "white!important",
              "& .fc-toolbar-title": {
                fontSize: "0.95rem!important",
              },
              "& .fc-view-harness-active": {
                margin: "0 6px!important",
              },
            }}
          >
            <JobsSchedulesCalendar
              showCustomEvents={showCustom}
              jobIds={null}
              crewIds={Object.keys(checked).filter((key) => checked[key]?.checked)}
              projectManagerIds={selectedProjectManagers}
              compactToolbar={true}
              useViewMenu={true}
              calendarHeight="100%"
            />
          </MDBox>
        </Paper>
      </MDBox>

      <Modal open={open} onClose={onClose}>
        <CreateUpdateCustomEvent action="create" onClose={onClose} />
      </Modal>

      <Modal open={!!selectedPhaseId} onClose={() => setSelectedPhaseId(null)}>
        {selectedPhaseId && (
          <ScheduleJobPhase
            id={selectedPhaseId}
            onClose={() => setSelectedPhaseId(null)}
            onScheduleSuccess={triggerCalendarUpdate}
          />
        )}
      </Modal>

      <JobModal jobId={selectedJobId} open={openJobModal} onClose={() => setOpenJobModal(false)} />
    </>
  );
}
